import { Component, DestroyRef, Inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { CTResultsService } from '@cloud-twin-v2/cloud-twin-shared/core/results/ct-results.service';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { AttributeShortInfo } from '@cloud-twin-v2/ct-map/models/attribute-short-info';
import { CtResult } from '@cloud-twin-v2/ct-map/models/ct-result';
import { NetworkElementResult } from '@cloud-twin-v2/ct-map/models/network-element-result';
import { ElementTypeCategoriesDto, WorkspaceConnectSettingDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { ScenarioItem } from '@cloud-twin-v2/scenarios-configuration/scenario-item';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutStructure } from '@common-modules/dynamic-layout/models/dynamic-layout-structure';
import { DynamicLayoutService } from '@common-modules/dynamic-layout/services/dynamic-layout.service';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateAreas } from '@common-modules/redux/models/state-areas';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { Observable, ReplaySubject, Subscription, combineLatest, map, take } from 'rxjs';
import { MapDateRange } from "@common-modules/wlm-map/models/map-date-range";

export const GRID_CONTAINER_WIDGET_COMPONENT_INSTANCE = `CtMapGridContainerWidgetComponent#2`;

@Component({
  selector: 'ct-map-grid-container-widget',
  templateUrl: './ct-map-grid-container-widget.component.html',
  styleUrl: './ct-map-grid-container-widget.component.scss',
  standalone: false,
  providers: [ReduxStateService, DynamicLayoutService],
})
export class CtMapGridContainerWidgetComponent extends BaseDynamicWidgetComponent {
  connectSettings: WorkspaceConnectSettingDto[];
  layoutSettings: DynamicLayoutSettings;

  private readonly _layoutKey = 'GridContainer';
  private readonly _defaultWidgetPage = 'MapPage';
  private readonly _defaultStructure: DynamicLayoutStructure[] = [];
  private readonly _baseSettings: SimpleDynamicLayoutSettings = {
    layoutKey: this._layoutKey,
    layoutArea: 'Main',
    scopeInstanceKeys: {
      [StateAreas.Generic]: 'shared',
    },
  };
  selectedScenarios: string[] = [];
  currentTimeStep: Date;
  selectedMetadata$: ReplaySubject<CtMetadata> = new ReplaySubject<CtMetadata>(1);
  selectedResults$: ReplaySubject<CtResult[]> = new ReplaySubject<CtResult[]>(1);
  resultsSubscription: Subscription | null = null;
  metadataSubscription: Subscription | null = null;
  gridsCreated: boolean = false;
  hiddenNeTypeLayers: string[] = [];
  availableTypes: string[];
  private setLoading: (loading: boolean) => void;
  private _mapDateRange: MapDateRange;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private readonly _selectedWorkspaceService: SelectedWorkspaceService,
    private readonly _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private readonly _ctAttributes: CtAttributesService,
    private readonly _ctResults: CTResultsService,
    private readonly _destroyRef: DestroyRef,
    private readonly _spinnerService: SpinnerService,
    private readonly _dateHelper: DateHelperService
  ) {
    super(injector, widgetSettings);
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  get componentName(): string {
    return 'CtMapGridContainerWidgetComponent';
  }

  onWidgetInit(): void {
    this._selectedWorkspaceService.selectedWorkspace$.subscribe((workspace) => {
      if (workspace) {
        this.connectSettings = workspace.workspaceConnectSettings;
        this.setSelectedScenarios();
        this._ctAttributes.getDateRange(this.selectedScenarios).subscribe( dateRange => {
            this._mapDateRange =  dateRange;
        });
      }
    });

    var metadata$ = this._ctAttributes.metadata$.pipe(take(1));
    var results$ = this._ctResults.results$;

    metadata$.subscribe(metadata => {
      this.selectedMetadata$.next(metadata);
    });

    results$.subscribe(results => {
      this.selectedResults$.next(results);
    });

    this.listenMapTimeSteps();
    this.listenLayersVisibility();
    this.listenScenarioVisibility();
  }

  listenScenarioVisibility() {
    this._state
      .select<ScenarioItem>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.ScenarioVisibility,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value: ScenarioItem) => {
        if (!value) {
          return;
        }

        if (value.isVisible) {
          this.selectedScenarios.push(value.scenarioId);
        } else {
          this.selectedScenarios = this.selectedScenarios.filter((f) => f != value.scenarioId);
        }

        this.gridsCreated = false;
        this.refreshGrids(this.currentTimeStep);
      });
  }

  setSelectedScenarios() {
    this.selectedScenarios = this.connectSettings.map((m) => m.scenarioId);
  }

  buildGridWidgets(selectedMetadata: CtMetadata) {
    this.setLoading(true);
    //filter selectedMetadata by selectedScenarios
    const filteredMetadata = this.getMetadataFromSelectedScenarios(selectedMetadata);

    const attributesByType = this.groupMetadataByType(filteredMetadata);
    this.availableTypes = Object.keys(attributesByType);

    if (this.hiddenNeTypeLayers.length) {
      this.hiddenNeTypeLayers.forEach((hiddenType) => {
        delete attributesByType[hiddenType];
      });
    }

    this.buildGroupItemLayoutSettings(attributesByType);

    this.buildDynamicLayoutSettings().subscribe((settings) => {
      this.layoutSettings = settings;
      this.gridsCreated = true;
      this.setLoading(false);
    });
  }

  private getMetadataFromSelectedScenarios(selectedMetadata: CtMetadata) {
    return Object.entries(selectedMetadata)
      .filter(([scenarioId]) => this.selectedScenarios.includes(scenarioId))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {} as { [scenarioId: string]: ElementTypeCategoriesDto[] });
  }

  private listenMapTimeSteps(): void {
    this._state
      .select<Date>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.MapStep,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value: Date) => {
        if (value && value !== this.currentTimeStep) {
          this.currentTimeStep = value;
          const dateRange = this._ctAttributes.getResultLoadDateRange(value, this._mapDateRange);
          this._ctResults.update(this.selectedScenarios, value, dateRange.startDate, dateRange.endDate)
          .subscribe({
            next: (result)=>{
              this.refreshGrids(value);
            },
            error: (error) =>{
            }
          });
          
        }
      });
  }

  private refreshGrids(value: Date) {
    this.initializeResults(value);

    if (!this.gridsCreated) {
      this.initializeGrids();
    }
  }

  private initializeResults(value: Date) {
    this.resultsSubscription?.unsubscribe();
    this.resultsSubscription = this.selectedResults$.subscribe((selectedResults) => {
      const neDataByType = this.getResultsByTypeAndTimeStep(selectedResults, value);

      this._state.dispatch(
        new SetValueAction({
          fieldName: MasterOperatorFields.AttributeResults,
          value: neDataByType,
        })
      );
    });
  }

  private initializeGrids() {
    this.metadataSubscription?.unsubscribe();
    this.metadataSubscription = this.selectedMetadata$.subscribe((selectedResults) => {
      this.buildGridWidgets(selectedResults);
    });
  }

  private listenLayersVisibility(): void {
    this._state
      .select<{ [type: string]: boolean }>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.LayersVisibility,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((layerVisibility: { [type: string]: boolean }) => {
        if (!layerVisibility) {
          return;
        }

        //in case the layersVisibility is dispatched before the metadata call
        if (!this.availableTypes) {
          this.hiddenNeTypeLayers = Object.keys(layerVisibility)
            .filter(key => layerVisibility[key] === false);

          return;
        }

        const types = Object.keys(layerVisibility);

        let rebuildGrids = false;
        types.forEach((type) => {
          if (this.availableTypes?.includes(type)) {
            if (layerVisibility[type]) {
              // if visibility is true --> remove type from hidden types
              this.hiddenNeTypeLayers = this.hiddenNeTypeLayers.filter((f) => f !== type);
            } else {
              this.hiddenNeTypeLayers.push(type);
            }

            rebuildGrids = true;
          }
        });

        if (rebuildGrids) {
          this.initializeGrids();
        }
      });
  }

  private buildDynamicLayoutSettings(): Observable<DynamicLayoutSettings> {
    const simpleSettings = globalUtilsHelper.clone(this._baseSettings, true);

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((dynamicLayoutSettings) => {
        dynamicLayoutSettings.showResetIcon = true;
        dynamicLayoutSettings.defaultStructure = this._defaultStructure;
        dynamicLayoutSettings.widgetPage = this._defaultWidgetPage;
        return dynamicLayoutSettings;
      })
    );
  }

  private buildGroupItemLayoutSettings(types: { [type: string]: AttributeShortInfo[] }): void {
    const items: SimpleDynamicLayoutSettingsItem[] = [];

    Object.entries(types).forEach(([type, attributes]) => {
      const componentName = 'CtTypeAttributeGridWidgetComponent';

      const item = {
        widgetName: componentName,
        titleKey: type,
        id: `${componentName}#${type}`,
        params: {
          type,
          attributes,
        },
      } as SimpleDynamicLayoutSettingsItem;

      items.push(item);
    });

    this._baseSettings.items = items;
  }

  private groupMetadataByType(input: { [scenarioId: string]: ElementTypeCategoriesDto[] }): {
    [type: string]: AttributeShortInfo[];
  } {
    const result: { [type: string]: AttributeShortInfo[] } = {};

    Object.entries(input).forEach(([scenarioId, elementTypeCategories]) => {
      elementTypeCategories.forEach(elementTypeCategory => {
        const type = elementTypeCategory.type;
        const categories = elementTypeCategory.categories;

        if (!type || !categories || categories.length === 0) {
          return;
        }

        if (!result[type]) {
          result[type] = [];
        }

        categories.forEach((category) => {
          if (category.attributes && category.attributes.length > 0) {
            const attributeInfos = category.attributes.map((attr) => ({
              field: attr.name,
              alias: attr.alias,
              units: attr.units,
              format: attr.format,
              scenarioId: scenarioId,
              attributeTypeKey: attr.attributeTypeKey
            }));

            result[type] = [...result[type], ...attributeInfos];
          }
        });
      });
    });

    Object.keys(result).forEach((type) => {
      if (result[type].length === 0) {
        delete result[type];
      }
    });

    return result;
  }

  private getResultsByTypeAndTimeStep(results: CtResult[], currentStep: Date): { networkElementTypeId?: string; values: { [property: string]: string }[] }[] {

    const timeStepStr = this._dateHelper.toApiExactISO(currentStep);

    const filteredResults = results.filter((result) => {
      if (
        this.selectedScenarios.length > 0 &&
        !this.selectedScenarios.includes(result.scenarioId)
      ) {
        return false;
      }

      if (currentStep < result.startDate || currentStep > result.endDate) {
        return false;
      }

      return true;
    });

    const groupedByType: { [typeId: string]: { scenarioId: string; element: NetworkElementResult }[] } = {};

    filteredResults.forEach((result) => {
      result.networkElements.forEach((networkElement) => {
        const typeId = networkElement.networkElementTypeId || 'undefined';

        if (!groupedByType[typeId]) {
          groupedByType[typeId] = [];
        }

        groupedByType[typeId].push({
          scenarioId: result.scenarioId,
          element: networkElement
        });
      });
    });

    const result = Object.entries(groupedByType).map(([typeId, elementsWithScenario]) => {
      const values = elementsWithScenario.map(({ scenarioId, element }) => {
        const transformedElement: { [property: string]: string } = {
          networkElementId: element.networkElementId,
          scenarioId: scenarioId
        };
        const connectSettings = this.connectSettings.find(x => x.scenarioId == scenarioId);

        transformedElement['modelName'] = connectSettings.instanceName;
        transformedElement['modelVersionName'] = connectSettings.snapshotName;
        transformedElement['scenarioName'] = connectSettings.scenarioName;
        transformedElement['networkElementName'] = element.networkElementName;

        element.attributes.forEach((attr) => {
          if (attr.values[timeStepStr]) {
            transformedElement[attr.name] = attr.values[timeStepStr];
          }
        });

        return transformedElement;
      });

      return {
        networkElementTypeId: typeId !== 'undefined' ? typeId : undefined,
        values: values,
      };
    });

    return result;
  }
}
